import { useState, useCallback } from "react";

interface ContributionResult {
  // Employee contributions
  annualEmployeeContribution: number;
  perPaycheckEmployeeContribution: number;
  monthlyEmployeeContribution: number;

  // Employer contributions
  annualEmployerContribution: number;
  perPaycheckEmployerContribution: number;
  monthlyEmployerContribution: number;

  // Totals
  totalAnnualContribution: number;
  totalPerPaycheckContribution: number;
  totalMonthlyContribution: number;

  // Goal tracking
  goalReachDate: string;
}

interface ContributionParams {
  annualSalary: number;
  employeeContributionPct: number;
  employerMaxMatchPct: number;
  savingsGoal?: number;
  paychecksPerYear?: number;
}

export function useContributionCalculations() {
  const [calculationResult, setCalculationResult] =
    useState<ContributionResult>({
      annualEmployeeContribution: 0,
      perPaycheckEmployeeContribution: 0,
      monthlyEmployeeContribution: 0,

      annualEmployerContribution: 0,
      perPaycheckEmployerContribution: 0,
      monthlyEmployerContribution: 0,

      totalAnnualContribution: 0,
      totalPerPaycheckContribution: 0,
      totalMonthlyContribution: 0,

      goalReachDate: "",
    });

  const calculateContributions = useCallback(
    ({
      annualSalary,
      employeeContributionPct,
      employerMaxMatchPct,
      savingsGoal = 0,
      paychecksPerYear = 26,
    }: ContributionParams): ContributionResult => {
      // Annual calculations
      const annualEmployeeContribution =
        annualSalary * (employeeContributionPct / 100);

      // Employer maximum match calculation
      let annualEmployerContribution = 0;
      if (employeeContributionPct <= employerMaxMatchPct) {
        // If employee contributes less than or equal to employer max match, employer matches 100%
        annualEmployerContribution = annualEmployeeContribution;
      } else {
        // If employee contributes more than employer max match, employer only matches up to their max
        annualEmployerContribution = annualSalary * (employerMaxMatchPct / 100);
      }

      const totalAnnualContribution =
        annualEmployeeContribution + annualEmployerContribution;

      // Per paycheck calculations
      const perPaycheckEmployeeContribution =
        annualEmployeeContribution / paychecksPerYear;
      const perPaycheckEmployerContribution =
        annualEmployerContribution / paychecksPerYear;
      const totalPerPaycheckContribution =
        perPaycheckEmployeeContribution + perPaycheckEmployerContribution;

      // Monthly calculations => annual (12 mmonths)
      const monthlyEmployeeContribution = annualEmployeeContribution / 12;
      const monthlyEmployerContribution = annualEmployerContribution / 12;
      const totalMonthlyContribution =
        monthlyEmployeeContribution + monthlyEmployerContribution;

      // Calculate goal reach date
      let goalReachDate = "";
      if (savingsGoal > 0 && totalPerPaycheckContribution > 0) {
        // Calculate how many paychecks needed to reach the goal
        const paychecksNeeded = savingsGoal / totalPerPaycheckContribution;

        // Calculate the future date
        const today = new Date();
        const weeksToAdd = paychecksNeeded * (52 / paychecksPerYear);
        const futureDate = new Date(today);
        futureDate.setDate(today.getDate() + Math.round(weeksToAdd * 7));

        // Format the date as Month Year
        goalReachDate = futureDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric",
        });
      }

      const result = {
        annualEmployeeContribution,
        perPaycheckEmployeeContribution,
        monthlyEmployeeContribution,

        annualEmployerContribution,
        perPaycheckEmployerContribution,
        monthlyEmployerContribution,

        totalAnnualContribution,
        totalPerPaycheckContribution,
        totalMonthlyContribution,

        goalReachDate,
      };

      setCalculationResult(result);
      return result;
    },
    []
  );

  const calculateForPayPeriod = useCallback(
    ({
      annualSalary,
      employeeContributionPct,
      employerMaxMatchPct,
      payPeriods,
    }: {
      annualSalary: number;
      employeeContributionPct: number;
      employerMaxMatchPct: number;
      payPeriods: number;
    }) => {
      // Calculate per-period amounts
      const perPeriodSalary = annualSalary / payPeriods;
      const employeeContribution =
        perPeriodSalary * (employeeContributionPct / 100);

      // Employer match calculation
      let employerContribution = 0;
      if (employeeContributionPct <= employerMaxMatchPct) {
        employerContribution = employeeContribution;
      } else {
        employerContribution = perPeriodSalary * (employerMaxMatchPct / 100);
      }

      const totalContribution = employeeContribution + employerContribution;

      return {
        employeeContribution,
        employerContribution,
        totalContribution,
      };
    },
    []
  );

  return {
    calculationResult,
    calculateContributions,
    calculateForPayPeriod,
  };
}
